import React, { useCallback, useEffect, useState } from 'react';

import { ApiService } from 'js/src/client';
import { usePlaybackFunctionality } from 'js/src/components/app/audio-tab/usePlaybackFunctionality';
import Waveform from 'js/src/components/app/waveform/Waveform';
import type { WaveformData } from 'js/src/components/app/waveform/waveform-types';
import type { PlayableAudio } from 'js/src/data/media';
import { getPlayableAudioWaveform } from 'js/src/data/media';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { isError } from 'js/src/types';

export const WAVEFORM_NUMBER_OF_BARS = 100;
const NO_WAVEFORM_DATA: WaveformData = [];

type AudioWaveformPresentationProps = {
    media: PlayableAudio;
};

const AudioWaveformPresentation = ({ media }: AudioWaveformPresentationProps) => {
    const [waveformData, setWaveformData] = useState<WaveformData>(NO_WAVEFORM_DATA);
    const [fillPercentage, setFillPercentage] = useState(0);

    const { seek, isCurrentPlayingTrack, currentTimePercentage } = usePlaybackFunctionality(media);
    const missingUUIDErrorMesssage = 'Asset does not have uuid';

    const fetchWaveform = useCallback(() => {
        return new Promise<WaveformData>((resolve, reject) => {
            const waveform = getPlayableAudioWaveform(media);
            if (waveform) {
                resolve(waveform);
                return;
            }
            const uuid = media.uuid;
            if (!uuid) {
                reject(new Error(missingUUIDErrorMesssage));
                return;
            }
            ApiService.audioassetRetrieve({ path: { uuid: uuid } })
                .then((response) => {
                    if (response.data.waveform) {
                        resolve(response.data.waveform);
                    }
                })
                .catch((err: unknown) => {
                    ErrorReporter.capture(err);
                });
        });
    }, [media]);

    useEffect(() => {
        (async () => {
            let waveform = null;
            try {
                waveform = await fetchWaveform();
            } catch (err: unknown) {
                if (isError(err) && err.message !== missingUUIDErrorMesssage) {
                    ErrorReporter.capture(err);
                }
            }
            if (waveform) {
                setWaveformData(waveform.slice(0, WAVEFORM_NUMBER_OF_BARS));
            }
        })();
    }, [fetchWaveform]);

    useEffect(() => {
        const value = isCurrentPlayingTrack ? currentTimePercentage : 0;
        setFillPercentage(value);
    }, [currentTimePercentage, isCurrentPlayingTrack]);

    return <Waveform waveformData={waveformData} fillPercentage={fillPercentage} onSeek={seek} />;
};

const AudioWaveformMemoized = React.memo(AudioWaveformPresentation);
const AudioWaveform = AudioWaveformMemoized;

export default AudioWaveform;
